import type { TKey } from './ru';

/**
 * Узбекский (латиница) — язык админки по умолчанию.
 *
 * Тип `Record<TKey, string>` гарантирует, что здесь есть каждый ключ из
 * `ru.ts`: пропущенный ключ — ошибка компиляции, а не пустая строка в UI.
 * Апостроф в oʻ/gʻ — U+02BB (ʻ), как принято в узбекской латинице.
 */
export const uz: Record<TKey, string> = {
  // -- Umumiy -------------------------------------------------------------
  'app.name': 'LMS Admin',
  'common.cancel': 'Bekor qilish',
  'common.confirm': 'Tasdiqlash',
  'common.save': 'Saqlash',
  'common.create': 'Yaratish',
  'common.edit': 'Tahrirlash',
  'common.delete': 'Oʻchirish',
  'common.search': 'Qidirish',
  'common.add': 'Qoʻshish',
  'common.all': 'Barchasi',
  'common.actions': 'Amallar',
  'common.action': 'Amal',
  'common.status': 'Holat',
  'common.title': 'Nomi',
  'common.role': 'Rol',
  'common.noData': 'Maʼlumot yoʻq',
  'common.enable': 'Yoqish',
  'common.disable': 'Oʻchirish',
  'common.active': 'faol',
  'common.archived': 'arxiv',
  'common.toArchive': 'Arxivga',
  'common.unarchive': 'Qaytarish',
  'common.time': 'Vaqt',
  'common.reason': 'Sabab',
  'common.days': 'Kun',
  'common.currency': 'soʻm',
  'common.dayShort': 'kun',
  'common.hourShort': 'soat',

  // -- Navigatsiya va sarlavhalar -----------------------------------------
  'nav.dashboard': 'Boshqaruv paneli',
  'nav.channels': 'Kanallar',
  'nav.courses': 'Kurslar',
  'nav.calendar': 'Kalendar',
  'nav.payments': 'Toʻlovlar',
  'nav.users': 'Oʻquvchilar',
  'nav.kicks': 'Chiqarishlar',
  'nav.paymentMethods': 'Rekvizitlar',
  'nav.settings': 'Sozlamalar',
  'page.calendar': 'Nashr kalendari',
  'page.kicks': 'Chiqarishlar jurnali',
  'header.logout': 'Chiqish',
  'lang.switchLabel': 'Interfeys tili',
  'lang.uz': "O'zbekcha",
  'lang.ru': 'Русский',
  'access.denied': 'Bu boʻlimni koʻrish uchun huquqlaringiz yetarli emas.',
  'toast.close': 'Yopish',

  // -- Kirish -------------------------------------------------------------
  'login.subtitle': 'Maʼmurning Telegram akkaunti orqali kiring',
  'login.botUsernameMissing':
    'VITE_TELEGRAM_BOT_USERNAME frontend/.env.local faylida koʻrsatilmagan.',
  'login.checking': 'Tekshirilmoqda...',
  'login.failed': 'Kirish amalga oshmadi.',
  'login.devDivider': 'yoki dasturlash uchun',
  'login.password': 'Parol',
  'login.byPassword': 'Parol bilan kirish',
  'login.hideDev': 'Dev-kirishni yashirish',
  'login.adminsOnly': 'Kirish faqat maʼmurlar uchun',

  // -- Boshqaruv paneli ---------------------------------------------------
  'dashboard.activeStudents': 'Faol oʻquvchilar',
  'dashboard.revenue': 'Daromad (30 kun)',
  'dashboard.signups': 'Roʻyxatdan oʻtishlar (30 kun)',
  'dashboard.expiring7d': '7 kun ichida tugaydi',
  'dashboard.pendingPayments': 'Tekshiruvda',
  'dashboard.revenueByDay': 'Kunlar boʻyicha daromad',
  'dashboard.signupsByDay': 'Kunlar boʻyicha roʻyxatdan oʻtishlar',
  'dashboard.revenueLabel': 'Daromad',
  'dashboard.signupsLabel': 'Roʻyxatdan oʻtishlar',

  // -- Kanallar -----------------------------------------------------------
  'channels.registerTitle': 'Kanalni roʻyxatga olish',
  'channels.registerIntro': 'Kanal bot orqali roʻyxatga olinadi. Tartib:',
  'channels.step1': 'Yopiq Telegram kanal yarating.',
  'channels.step2': 'Botni barcha huquqlar bilan maʼmur qilib qoʻshing.',
  'channels.step3': 'Bot bilan shaxsiy chatda /register_channel buyrugʻini yuboring.',
  'channels.step4': 'Kanaldan istalgan xabarni botga forward qiling.',
  'channels.step5': 'Kanal pastdagi roʻyxatda paydo boʻladi.',
  'channels.emptyTitle': 'Kanallar yoʻq',
  'channels.emptyDescription': 'Birinchi kanalni bot orqali roʻyxatga oling.',
  'channels.registeredAt': 'Roʻyxatga olingan',

  // -- Kurslar ------------------------------------------------------------
  'courses.heading': 'Kurslar',
  'courses.new': 'Yangi kurs',
  'courses.emptyTitle': 'Kurslar yoʻq',
  'courses.emptyDescription': 'Birinchi kursni yarating.',
  'courses.channel': 'Kanal',
  'courses.method': 'Usul',
  'courses.grace': 'Grace muddati',
  'courses.reminders': 'Eslatmalar',
  'courses.dialogEdit': 'Kurs: {title}',
  'courses.formChannel': 'Kanal',
  'courses.formChannelLocked': 'bogʻlanish oʻzgarmaydi',
  'courses.formChannelPick': '— tanlang —',
  'courses.formTitle': 'Nomi',
  'courses.formDescription': 'Tavsif',
  'courses.formPrice': 'Narxi (soʻm)',
  'courses.formDuration': 'Davomiyligi (kun)',
  'courses.formAccessMethod': 'Kirish usuli',
  'courses.formGrace': 'Grace muddati (soat)',
  'courses.formRules': 'Kurs qoidalari',
  'courses.created': 'Kurs yaratildi',
  'courses.updated': 'Kurs yangilandi',
  'courses.saveFailed': 'Saqlab boʻlmadi',
  'courses.toggleFailed': 'Holatni oʻzgartirib boʻlmadi',

  // -- Nashr kalendari ----------------------------------------------------
  'calendar.monthCaption': '{month} uchun nashr kalendari.',
  'calendar.addMaterial': 'Material qoʻshish',
  'calendar.emptyTitle': 'Bu oyda materiallar yoʻq',
  'calendar.emptyNoCourses': 'Avval «Kurslar» sahifasida kurs yarating.',
  'calendar.emptyHint': 'Nashrni rejalashtirish uchun «Material qoʻshish» tugmasini bosing.',
  'calendar.untitled': '(nomsiz)',
  'calendar.reschedule': 'Koʻchirish',
  'calendar.cancelPublish': 'Bekor qilish',
  'calendar.confirmCancel': 'Material nashri bekor qilinsinmi?',
  'calendar.materialDeleted': 'Material oʻchirildi',
  'calendar.deleteFailed': 'Oʻchirib boʻlmadi',
  'calendar.rescheduled': 'Nashr sanasi yangilandi',
  'calendar.rescheduleFailed': 'Koʻchirib boʻlmadi',
  'calendar.rescheduleTitle': 'Materialni koʻchirish',
  'calendar.rescheduleDescription': 'Yangi nashr sanasi va vaqti',
  'calendar.statusCancelled': 'bekor qilindi',
  'calendar.statusPublished': 'nashr etildi',
  'calendar.statusError': 'xatolik',
  'calendar.statusPending': 'kutilmoqda',
  'calendar.newMaterial': 'Yangi material',
  'material.typeVideo': 'Video',
  'material.typeDocument': 'Hujjat',
  'material.typePhoto': 'Rasm',
  'material.typeAudio': 'Audio',
  'material.typeVoice': 'Ovozli xabar',
  'material.typeText': 'Matn',
  'material.course': 'Kurs',
  'material.courseArchived': '(arxiv)',
  'material.type': 'Turi',
  'material.titleForAdmin': 'Nomi (admin panel uchun)',
  'material.titlePlaceholder': '1-dars: Kirish',
  'material.file': 'Fayl',
  'material.uploadedToTelegram': 'Telegramga yuklandi',
  'material.changeFile': 'Almashtirish',
  'material.usingExistingFileId': 'Tayyor file_id ishlatilmoqda ({prefix}…)',
  'material.uploading': 'Telegramga yuklanmoqda…',
  'material.uploadingProgress': 'Serverga yuklanmoqda… {percent}%',
  'material.pickFile': 'Fayl tanlash uchun bosing',
  'material.fileHint': 'video / hujjat / rasm / audio · {limit} gacha',
  'material.tooLarge': 'Fayl juda katta ({size}). Limit — {limit}.',
  'material.uploadFailed': 'Faylni yuklab boʻlmadi',
  'material.uploadFailedWith': 'Faylni yuklab boʻlmadi: {message}',
  'material.manualFileId': 'Tayyor file_id dan foydalanish (qoʻshimcha)',
  'material.fileUniqueIdOptional': 'file_unique_id (majburiy emas)',
  'material.caption': 'Izoh (caption)',
  'material.captionPlaceholder': 'Media ostidagi tavsif',
  'material.textBody': 'Xabar matni',
  'material.textBodyPlaceholder': 'Kanalda nashr etiladigan matn',
  'material.scheduledAt': 'Nashr sanasi va vaqti',
  'material.tzHint':
    'Vaqt qurilmangiz vaqt mintaqasida. Nashr — server vaqti boʻyicha koʻrsatilgan paytda.',
  'material.pin': 'Kanalda mahkamlash',
  'material.silent': 'Bildirishnomasiz',
  'material.scheduled': 'Material rejalashtirildi',
  'material.createFailed': 'Yaratib boʻlmadi',

  // -- Toʻlovlar ----------------------------------------------------------
  'payments.statusPending': 'Kutilmoqda',
  'payments.statusApproved': 'Tasdiqlangan',
  'payments.statusRejected': 'Rad etilgan',
  'payments.statusCancelled': 'Bekor qilingan',
  'payments.emptyTitle': 'Toʻlovlar yoʻq',
  'payments.emptyDescription': 'Bu holatda hozircha hech narsa yoʻq.',
  'payments.student': 'Oʻquvchi',
  'payments.course': 'Kurs',
  'payments.amount': 'Summa',
  'payments.receivedAt': 'Qabul qilindi',
  'payments.approve': 'Tasdiqlash',
  'payments.reject': 'Rad etish',
  'payments.issueAccess': 'Kirish berish',
  'payments.issueAccessHint': 'Invite-havola yaratib oʻquvchiga yuborish',
  'payments.accessGranted': 'Kirish berilgan',
  'payments.accessGrantedAt': 'Kirish {when} da berilgan',
  'payments.approved': 'Toʻlov tasdiqlandi, havola oʻquvchiga yuborildi',
  'payments.approveFailed': 'Tasdiqlab boʻlmadi',
  'payments.rejected': 'Toʻlov rad etildi, oʻquvchi xabardor qilindi',
  'payments.rejectFailed': 'Rad etib boʻlmadi',
  'payments.inviteSent': 'Taklif havolasi oʻquvchiga yuborildi',
  'payments.issueFailed': 'Kirish berib boʻlmadi: {message}',
  'payments.alreadyGranted': 'Kirish allaqachon {when} da berilgan.',
  'payments.reissueConfirm': 'Shunga qaramay havola qayta chiqarilsinmi?',
  'payments.receiptTitle': 'Chek #{id}',
  'payments.receiptLoadFailed': 'Chekni yuklab boʻlmadi: {error}',
  'payments.receiptComment': 'Izoh: {caption}',
  'payments.rejectTitle': '#{id} toʻlovni rad etish',
  'payments.rejectDescription': 'Sabab oʻquvchiga Telegram orqali yuboriladi.',
  'payments.rejectPlaceholder': 'Masalan: chek oʻqilmaydi, summa notoʻgʻri...',

  // -- Oʻquvchilar --------------------------------------------------------
  'users.searchPlaceholder': 'Ism, @username, telefon, ID...',
  'users.allStatuses': 'Barcha holatlar',
  'users.emptyTitle': 'Oʻquvchilar yoʻq',
  'users.emptyDescription': 'Tanlangan filtrlar boʻyicha hech kim topilmadi.',
  'users.fullName': 'F.I.Sh.',
  'users.region': 'Viloyat',
  'users.registeredAt': 'Roʻyxatdan oʻtgan',
  'users.totalShown': 'Jami: {total}. Koʻrsatilgan: {shown}.',

  // -- Oʻquvchi kartochkasi -----------------------------------------------
  'userCard.notFound': 'Foydalanuvchi topilmadi.',
  'userCard.backToList': 'Roʻyxatga',
  'userCard.block': 'Bloklash',
  'userCard.unblock': 'Blokdan chiqarish',
  'userCard.age': 'Yoshi',
  'userCard.phone': 'Telefon',
  'userCard.rulesAcceptedAt': 'Qoidalar qabul qilingan',
  'userCard.subscriptions': 'Obunalar ({count})',
  'userCard.noSubscriptions': 'Obunalar yoʻq.',
  'userCard.subActive': 'faol',
  'userCard.subFinished': 'tugagan',
  'userCard.cancelReason': 'Bekor qilish sababi: {reason}',
  'userCard.extend': 'Uzaytirish',
  'userCard.paymentHistory': 'Toʻlovlar tarixi ({count})',
  'userCard.noPayments': 'Toʻlovlar yoʻq.',
  'userCard.extendTitle': '«{title}» obunasini uzaytirish',
  'userCard.extendDescription': 'Obuna koʻrsatilgan kunlar soniga uzaytiriladi.',
  'userCard.blockTitle': 'Oʻquvchini bloklash',
  'userCard.blockDescription':
    'Foydalanuvchi barcha kanallardan chiqariladi va yangi kurslarni toʻlay olmaydi.',
  'userCard.blockReason': 'Sabab (majburiy)',

  // -- Chiqarishlar -------------------------------------------------------
  'kicks.emptyTitle': 'Chiqarishlar boʻlmagan',
  'kicks.student': 'Oʻquvchi',
  'kicks.channel': 'Kanal',
  'kicks.restored': 'tiklangan',
  'kicks.kicked': 'chiqarilgan',
  'kicks.restore': 'Tiklash',
  'kicks.restoreTitle': 'Kirishni tiklash',
  'kicks.restoreDescription':
    'Obuna qayta faollashtiriladi va koʻrsatilgan kunlar soniga uzaytiriladi. Foydalanuvchi kanalga qaytish uchun havola oladi.',
  'kicks.extendDays': 'Uzaytirish kunlari',

  // -- Rekvizitlar --------------------------------------------------------
  'methods.title': 'Toʻlov qabul qilish rekvizitlari',
  'methods.description':
    'Bu maʼlumotlar oʻquvchilarga toʻlov paytida koʻrsatiladi. Oʻchirilgan usullar botda koʻrinmaydi.',
  'methods.emptyTitle': 'Rekvizitlar sozlanmagan',
  'methods.emptyDescription':
    'Birinchi toʻlov usulini qoʻshing — usiz bot cheklarni qabul qila olmaydi.',
  'methods.card': 'Karta',
  'methods.holder': 'Karta egasi',
  'methods.bank': 'Bank',
  'methods.statusOn': 'faol',
  'methods.statusOff': 'oʻchirilgan',
  'methods.confirmDelete': '«{title}» oʻchirilsinmi?',
  'methods.deleted': 'Rekvizitlar oʻchirildi',
  'methods.deleteFailed': 'Oʻchirib boʻlmadi',
  'methods.created': 'Rekvizitlar yaratildi',
  'methods.updated': 'Rekvizitlar yangilandi',
  'methods.toggleFailed': 'Holatni oʻzgartirib boʻlmadi',
  'methods.saveFailed': 'Saqlab boʻlmadi',
  'methods.newTitle': 'Yangi toʻlov usuli',
  'methods.editTitle': 'Tahrirlash: {title}',
  'methods.formTitle': 'Nomi *',
  'methods.formTitlePlaceholder': 'Masalan: «Humo kartasiga oʻtkazma»',
  'methods.formCard': 'Karta raqami',
  'methods.formCardError': 'Raqam 13–19 ta raqamdan iborat boʻlishi kerak',
  'methods.formHolder': 'Karta egasi',
  'methods.formBank': 'Bank',
  'methods.formBankPlaceholder': 'Kapital Bank',
  'methods.formSortOrder': 'Koʻrsatish tartibi',
  'methods.formInstructions': 'Oʻquvchi uchun koʻrsatma (majburiy emas)',
  'methods.formInstructionsPlaceholder':
    'Masalan: «Toʻlov izohida Telegram username’ingizni yozing»',
  'methods.formActive': 'Faol (botda oʻquvchilarga koʻrsatish)',

  // -- Sozlamalar ---------------------------------------------------------
  'settings.promoteTitle': 'Maʼmur tayinlash',
  'settings.promoteDescription':
    'Faqat super_admin boshqa foydalanuvchilarga admin rollarini tayinlashi mumkin.',
  'settings.telegramId': 'Telegram ID',
  'settings.roleModerator': 'moderator — faqat toʻlovlarni tekshirish',
  'settings.roleAdmin': 'admin — kurslar va oʻquvchilarni boshqarish',
  'settings.roleSuperAdmin': 'super_admin — toʻliq huquq',
  'settings.promote': 'Tayinlash',
  'settings.demoteTitle': 'Maʼmur rolini olib tashlash',
  'settings.demote': 'Rolni olib tashlash',
  'settings.done': 'Bajarildi.',
};
